import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate, formatDateTime } from "@/lib/format";
import { OwnerPicker } from "./OwnerPicker";
import { ROUTE_BADGES } from "./dashboardTokens";

/** Click-to-call and click-to-email, because these people are waiting. */
const ContactCell = ({ row }) => (
  <TableCell className="text-sm">
    <span className="block font-medium">{row.full_name}</span>
    <span className="block text-xs text-muted-foreground">{row.company}</span>
    <span className="block text-xs text-muted-foreground">
      <a
        href={`mailto:${row.email}`}
        className="underline-offset-2 hover:text-primary hover:underline focus-visible:text-primary focus-visible:underline"
        data-testid="admin-escalation-email-link"
      >
        {row.email}
      </a>{" "}
      ·{" "}
      <a
        href={`tel:${row.phone}`}
        className="underline-offset-2 hover:text-primary hover:underline focus-visible:text-primary focus-visible:underline"
        data-testid="admin-escalation-phone-link"
      >
        {row.phone_display}
      </a>
    </span>
  </TableCell>
);

/** Which rule sent them here, plus anything they typed. */
const RouteCell = ({ row }) => {
  const badge = ROUTE_BADGES[row.route] || ROUTE_BADGES.self_serve;
  return (
    <TableCell className="max-w-[300px]">
      <Badge
        variant="outline"
        data-testid="admin-escalation-route-badge"
        className={`mb-1.5 ${badge.className}`}
      >
        {badge.label}
      </Badge>
      <span
        className="block text-xs leading-relaxed text-muted-foreground"
        data-testid="admin-escalation-reasons"
      >
        {row.reasons?.length
          ? row.reasons.map((reason) => reason.message).join(" · ")
          : "Didn't fit any of the three flows"}
      </span>
      {row.message ? (
        <span
          className="mt-1 block border-l-2 border-border pl-2 text-xs italic leading-relaxed text-muted-foreground"
          data-testid="admin-escalation-message"
        >
          {row.message}
        </span>
      ) : null}
    </TableCell>
  );
};

/** How long they have been waiting against the response promise. */
const WaitingCell = ({ row }) => (
  <TableCell className="max-w-[190px]">
    {row.overdue ? (
      <Badge
        variant="outline"
        data-testid="admin-escalation-overdue-badge"
        className="mb-1 border-destructive/30 bg-destructive/10 text-destructive"
      >
        <AlertTriangle className="mr-1 h-3 w-3" aria-hidden="true" />
        Overdue
      </Badge>
    ) : null}
    <span className="block text-xs text-muted-foreground" data-testid="admin-escalation-waiting">
      {row.handled ? `Answered in ${row.waited_label}` : `Waiting ${row.waited_label}`}
    </span>
    {!row.handled && row.due_label ? (
      <span className="block text-[11px] text-muted-foreground">
        {row.overdue ? row.overdue_label : `Due by ${row.due_label}`}
      </span>
    ) : null}
  </TableCell>
);

/** Spinner while saving, a tick once handled, otherwise the invitation. */
const HandledLabel = ({ row, saving }) => {
  if (saving) return <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />;
  if (row.handled) {
    return (
      <>
        <Check className="mr-1.5 h-4 w-4" aria-hidden="true" />
        Handled
      </>
    );
  }
  return "Mark handled";
};

/** Whose name is against this, and when it was handed over. */
const OwnerCell = ({ row, team, savingOwner, onAssignOwner, onAddTeamMember }) => (
  <TableCell className="align-top">
    <OwnerPicker
      value={row.owner || ""}
      members={team}
      saving={savingOwner === row.reference}
      onAssign={(owner) => onAssignOwner(row.reference, owner)}
      onAddMember={onAddTeamMember}
      testId="admin-escalation-owner"
    />
    {row.owner && row.owner_at ? (
      <span
        className="mt-1 block text-[11px] text-muted-foreground"
        data-testid="admin-escalation-owner-stamp"
      >
        Assigned {formatDate(row.owner_at)}
      </span>
    ) : null}
  </TableCell>
);

/** Everyone who needs a person to get back to them. */
export const EscalationsTable = ({
  rows,
  savingReference,
  onToggleHandled,
  team = [],
  savingOwner,
  onAssignOwner,
  onAddTeamMember,
}) => (
  <Table data-testid="admin-fallbacks-table">
    <TableHeader>
      <TableRow>
        <TableHead>Date</TableHead>
        <TableHead>Contact</TableHead>
        <TableHead>Was quoting</TableHead>
        <TableHead>How they got here</TableHead>
        <TableHead>Waiting</TableHead>
        <TableHead>Assigned to</TableHead>
        <TableHead className="text-right">Handled</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.map((row) => (
        <TableRow
          key={row.reference}
          data-testid="admin-fallback-row"
          className={row.handled ? "opacity-70" : undefined}
        >
          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
            {formatDateTime(row.created_at)}
            <span className="mt-0.5 block font-amount">{row.reference}</span>
          </TableCell>
          <ContactCell row={row} />
          <TableCell className="text-sm">
            {row.service_label || <span className="text-muted-foreground">General enquiry</span>}
          </TableCell>
          <RouteCell row={row} />
          <WaitingCell row={row} />
          <OwnerCell
            row={row}
            team={team}
            savingOwner={savingOwner}
            onAssignOwner={onAssignOwner}
            onAddTeamMember={onAddTeamMember}
          />
          <TableCell className="text-right">
            <Button
              type="button"
              size="sm"
              variant={row.handled ? "outline" : "default"}
              disabled={savingReference === row.reference}
              onClick={() => onToggleHandled(row.reference, !row.handled)}
              data-testid="admin-escalation-handled-button"
            >
              <HandledLabel row={row} saving={savingReference === row.reference} />
            </Button>
            {row.handled && row.handled_at ? (
              <span className="mt-1 block text-[11px] text-muted-foreground">
                {formatDate(row.handled_at)}
                {row.handled_by ? ` · ${row.handled_by}` : ""}
              </span>
            ) : null}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
